import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { QuickLink } from "../../types/quickLink";
import {
  createQuickLink,
  deleteQuickLink,
  getQuickLinks,
  updateQuickLink,
} from "../api/quickLinks";

export const useQuickLinks = () => {
  const queryClient = useQueryClient();

  /**
   * Fetch all quick links for the authenticated user
   */
  const {
    data: quickLinks,
    isLoading: isLoadingQuickLinks,
    isError: isErrorQuickLinks,
    error: quickLinksError,
  } = useQuery<QuickLink[], Error>({
    queryKey: ["quickLinks"],
    queryFn: getQuickLinks,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: true,
  });

  /**
   * Mutation for creating a new quick link
   */
  const createQuickLinkMutation = useMutation<
    QuickLink,
    Error,
    Omit<QuickLink, "id" | "user">
  >({
    mutationFn: createQuickLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quickLinks"] });
    },
    onError: (error) => {
      console.error("Error creating quick link:", error);
    },
  });

  /**
   * Mutation for updating a quick link
   */
  const updateQuickLinkMutation = useMutation<
    QuickLink,
    Error,
    { linkData: Partial<Omit<QuickLink, "user">> }
  >({
    mutationFn: ({ linkData }) => updateQuickLink(linkData),
    onMutate: async ({ linkData }) => {
      await queryClient.cancelQueries({ queryKey: ["quickLinks"] });

      const previousQuickLinks = queryClient.getQueryData<QuickLink[]>([
        "quickLinks",
      ]);

      // Optimistically update the cache
      if (previousQuickLinks) {
        queryClient.setQueryData<QuickLink[]>(
          ["quickLinks"],
          previousQuickLinks.map((link) =>
            link.id === linkData.id ? { ...link, ...linkData } : link
          )
        );
      }

      return { previousQuickLinks };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["quickLinks"] });
    },
  });

  /**
   * Mutation for deleting a quick link
   */
  const deleteQuickLinkMutation = useMutation<
    { id: number } | null,
    Error,
    number,
    { previousQuickLinks: QuickLink[] | undefined }
  >({
    mutationFn: deleteQuickLink,
    onMutate: async (linkId) => {
      await queryClient.cancelQueries({ queryKey: ["quickLinks"] });

      const previousQuickLinks = queryClient.getQueryData<QuickLink[]>([
        "quickLinks",
      ]);

      // Optimistically update the cache
      queryClient.setQueryData<QuickLink[]>(["quickLinks"], (old) =>
        old ? old.filter((link) => link.id !== linkId) : []
      );

      return { previousQuickLinks };
    },
    onError: (error, linkId, context) => {
      console.error("Error deleting quick link:", error || linkId);
      // Rollback to previous data
      if (context?.previousQuickLinks) {
        queryClient.setQueryData(["quickLinks"], context.previousQuickLinks);
      }
    },
    onSettled: () => {
      // Invalidate to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ["quickLinks"] });
    },
  });
  /**
   * Function to create a new quick link
   *
   * @param linkData The data for the new quick link
   */
  const addQuickLink = (linkData: Omit<QuickLink, "id" | "user">) => {
    createQuickLinkMutation.mutate(linkData);
  };

  /**
   * Function to update an existing quick link
   *
   * @param linkId The ID of the quick link to update
   * @param linkData The updated data for the quick link
   */
  const modifyQuickLink = (linkData: Partial<Omit<QuickLink, "user">>) => {
    updateQuickLinkMutation.mutate({ linkData });
  };

  /**
   * Function to delete a quick link
   *
   * @param linkId The ID of the quick link to delete
   */
  const removeQuickLink = (linkId: number) => {
    deleteQuickLinkMutation.mutate(linkId);
  };

  return {
    quickLinks,
    isLoadingQuickLinks,
    isErrorQuickLinks,
    quickLinksError,
    addQuickLink,
    modifyQuickLink,
    removeQuickLink,
    isCreating: createQuickLinkMutation.isPending,
    isUpdating: updateQuickLinkMutation.isPending,
    isDeleting: deleteQuickLinkMutation.isPending,
  };
};
